import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'
import type {
  BesprechungFortschreibungAntwort,
  BesprechungKonfig,
  BesprechungOffenerPunkt,
  BesprechungTop,
  MailExportResult,
  MailTransportChoice,
  SiteDocDetail,
  SiteDocProjectContact,
} from '@symphenia/shared-types'

/**
 * AP-49 12. — **Die Wege der Projektbesprechung** im ERP (`/site-docs/meeting`). Anlegen, Kopf, Fotos,
 * Abschluss und Storno gehen über die Wege des Rahmens (`api/site-docs.api.ts`).
 */
const basis = '/site-docs/meeting'

export interface BesprechungMitarbeiter {
  id: string
  name: string
  email: string | null
}

export const besprechungKonfig = () => api.get<{ data: BesprechungKonfig }>(`${basis}/config`).then((r) => r.data.data)

export const besprechungMitarbeiter = () => api.get<{ data: BesprechungMitarbeiter[] }>(`${basis}/people`).then((r) => r.data.data)

export const besprechungKontakte = (id: string) => api.get<{ data: SiteDocProjectContact[] }>(`${basis}/${id}/contacts`).then((r) => r.data.data)

export const besprechungFortschreibung = (q: { documentId?: string; art?: string; source?: string; projectId?: string | null; projectRefId?: string | null; text?: string }) =>
  api.get<{ data: BesprechungFortschreibungAntwort }>(`${basis}/carry-over`, { params: q }).then((r) => r.data.data)

export const besprechungKopf = (id: string, content: Record<string, unknown>, config?: { headers?: Record<string, string> }) =>
  api.patch<{ data: SiteDocDetail }>(`${basis}/${id}`, { content }, config).then((r) => r.data.data)

export const teilnehmerAnlegen = (id: string, body: Record<string, unknown>) => api.post<{ data: SiteDocDetail }>(`${basis}/${id}/participants`, body).then((r) => r.data.data)
export const teilnehmerAendern = (id: string, pid: string, body: Record<string, unknown>) => api.patch<{ data: SiteDocDetail }>(`${basis}/${id}/participants/${pid}`, body).then((r) => r.data.data)
export const teilnehmerLoeschen = (id: string, pid: string) => api.delete<{ data: SiteDocDetail }>(`${basis}/${id}/participants/${pid}`).then((r) => r.data.data)

export const punktAnlegen = (id: string, body: Record<string, unknown>) => api.post<{ data: SiteDocDetail }>(`${basis}/${id}/items`, body).then((r) => r.data.data)
export const punktAendern = (id: string, iid: string, body: Record<string, unknown>) => api.patch<{ data: SiteDocDetail }>(`${basis}/${id}/items/${iid}`, body).then((r) => r.data.data)
export const punktLoeschen = (id: string, iid: string) => api.delete<{ data: SiteDocDetail }>(`${basis}/${id}/items/${iid}`).then((r) => r.data.data)

export const tagesordnungSetzen = (id: string, tagesordnung: BesprechungTop[]) => api.patch<{ data: SiteDocDetail }>(`${basis}/${id}/agenda`, { tagesordnung }).then((r) => r.data.data)

export const berichtigungAnlegen = (id: string) => api.post<{ data: SiteDocDetail }>(`${basis}/${id}/correction`).then((r) => r.data.data)

/** „An den Verteiler senden" (7.1) — Formular wie jeder Mailversand; der Server teilt in Mails zu je 20. */
export const anVerteilerSenden = (id: string, mail: { to: string[]; subject: string; message: string; copyToSelf?: boolean; transport?: MailTransportChoice }, dateien: readonly File[] = []) => {
  const formular = new FormData()
  formular.append('mail', JSON.stringify(mail))
  for (const d of dateien) formular.append('files', d, d.name)
  return api.post<{ data: { mails: MailExportResult[]; recipients: number; sentAt: string } }>(`${basis}/${id}/distribute`, formular).then((r) => r.data.data)
}

export interface OffenePunkteParams extends Record<string, unknown> {
  projectId?: string
  projectRefId?: string
  art?: string
  q?: string
  overdue?: boolean
  internal?: boolean
  sort?: string
  dir?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export const offenePunkte = (params: OffenePunkteParams) =>
  api.get<{ data: BesprechungOffenerPunkt[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(`${basis}/open-items`, { params }).then((r) => r.data)

export const offenePunkteExport = (params: OffenePunkteParams & { format: string; orientation?: string; columns?: string }): ExportRequest => ({ url: `${basis}/open-items/export`, params })
